// src/pages/faq/Faq.js
import React, { useEffect, useState } from 'react'
import Container from 'react-bootstrap/Container'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Form from 'react-bootstrap/Form'
import InputGroup from 'react-bootstrap/InputGroup'
import Button from 'react-bootstrap/Button'
import Table from 'react-bootstrap/Table'
import Badge from 'react-bootstrap/Badge'
import Dropdown from 'react-bootstrap/Dropdown'
import DropdownButton from 'react-bootstrap/DropdownButton'
import Alert from 'react-bootstrap/Alert'
import Spinner from 'react-bootstrap/Spinner'
import { toast } from 'react-toastify'
import api from 'api/api'

import { debounce } from 'utils/debounce'
import { normalizarBooleano } from 'utils/general'
import TopHeader from 'components/TopHeader'
import Paginacion from 'components/Paginacion'

import NuevaPregunta from './NuevaPregunta'
import VerPregunta from './VerPregunta'
import EditarPregunta from './EditarPregunta'
import EliminarPregunta from './EliminarPregunta'

import './faq.css'

function Faq() {
	const [loading, setLoading] = useState(false)
	const [configuracion, setConfiguracion] = useState({ habilitado: false })
	const [preguntas, setPreguntas] = useState([])
	const [numResultados, setNumResultados] = useState(0)
	const [paginaAct, setPaginaAct] = useState(1)
	const [fetchDeACuantos, setFetchDeACuantos] = useState(10)

	const [busqueda, setBusqueda] = useState('')
	const [filtroBusqueda, setFiltroBusqueda] = useState('')
	const [filtroEstado, setFiltroEstado] = useState('')

	const [accionActual, setAccionActual] = useState({
		pregunta: null,
		accion: ''
	})

	// Estado para drag & drop
	const [draggedItem, setDraggedItem] = useState(null)
	const [dropTarget, setDropTarget] = useState(null)

	const opcionesCuantosMostrar = [10, 25, 50, 100]

	// Cargar configuración
	const refreshConfiguracion = async () => {
		try {
			const { datos } = await api.get('p/config-faq')
			setConfiguracion({
				...datos,
				habilitado: normalizarBooleano(datos.habilitado)
			})
		} catch (error) {
			toast.error('Error al cargar configuración')
			console.error(error)
		}
	}

	// Cargar preguntas
	const refreshPreguntas = async () => {
		try {
			setLoading(true)
			const filtros = {}

			if (filtroBusqueda) {
				filtros.busqueda = filtroBusqueda
			}

			if (filtroEstado !== '') {
				filtros.activo = filtroEstado === 'activo' ? 1 : 0
			}

			const { datos } = await api.post('p/ver-preguntas-frecuentes', {
				cuantos: fetchDeACuantos,
				desde: paginaAct === 1 ? 0 : (paginaAct - 1) * fetchDeACuantos,
				filtros
			})

			setPreguntas((datos.preguntas || []).map(p => ({
				...p,
				activo: normalizarBooleano(p.activo)
			})))
			setNumResultados(datos.numResultados || 0)
		} catch (error) {
			toast.error('Error al cargar preguntas')
			console.error(error)
		} finally {
			setLoading(false)
		}
	}

	// Toggle configuración habilitado/deshabilitado
	const toggleHabilitado = async (nuevoValor) => {
		try {
			const { msg } = await api.put('a/config-faq', { habilitado: nuevoValor })
			setConfiguracion({ ...configuracion, habilitado: nuevoValor })
			toast.success(msg || 'Configuración actualizada')
		} catch (error) {
			toast.error('Error al actualizar configuración')
			console.error(error)
		}
	}

	// Toggle estado activo/inactivo de una pregunta
	const toggleEstadoPregunta = async (id, nuevoEstado) => {
		try {
			const { msg } = await api.put(`a/preguntas-frecuentes/${id}`, { activo: nuevoEstado })
			refreshPreguntas()
			toast.success(msg || 'Estado actualizado')
		} catch (error) {
			toast.error('Error al actualizar estado')
			console.error(error)
		}
	}

	// CRUD operations
	const nuevaPregunta = async (data) => {
		try {
			const { msg } = await api.post('a/preguntas-frecuentes', data)
			refreshPreguntas()
			cerrarAccion()
			toast.success(msg || 'Pregunta creada correctamente')
		} catch (error) {
			console.error(error)
			toast.error('Error al crear la pregunta')
		}
	}

	const editarPregunta = async (data) => {
		try {
			const { msg } = await api.put(`a/preguntas-frecuentes/${accionActual.pregunta.id}`, data)
			refreshPreguntas()
			cerrarAccion()
			toast.success(msg || 'Pregunta actualizada correctamente')
		} catch (error) {
			console.error(error)
			toast.error('Error al actualizar la pregunta')
		}
	}

	const eliminarPregunta = async (id) => {
		try {
			const { msg } = await api.delete(`a/preguntas-frecuentes/${id}`)
			refreshPreguntas()
			cerrarAccion()
			toast.success(msg || 'Pregunta eliminada correctamente')
		} catch (error) {
			console.error(error)
			toast.error('Error al eliminar la pregunta')
		}
	}

	// Cambiar orden (drag & drop)
	const cambiarOrden = async (preguntaId, nuevoOrden) => {
		try {
			await api.put(`a/preguntas-frecuentes/${preguntaId}/orden`, { orden: nuevoOrden })
			refreshPreguntas()
		} catch (error) {
			toast.error('Error al actualizar el orden')
			console.error(error)
		}
	}

	// Handlers para drag & drop
	const handleDragStart = (e, pregunta) => {
		setDraggedItem(pregunta)
		e.dataTransfer.effectAllowed = 'move'
	}

	const handleDragOver = (e, pregunta) => {
		e.preventDefault()
		e.dataTransfer.dropEffect = 'move'

		if (draggedItem && draggedItem.id !== pregunta.id) {
			setDropTarget(pregunta.id)
		}
	}

	const handleDragLeave = (e) => {
		setDropTarget(null)
	}

	const handleDrop = async (e, targetPregunta) => {
		e.preventDefault()

		if (!draggedItem || draggedItem.id === targetPregunta.id) {
			setDraggedItem(null)
			setDropTarget(null)
			return
		}

		// Intercambiar órdenes
		await cambiarOrden(draggedItem.id, targetPregunta.orden)

		setDraggedItem(null)
		setDropTarget(null)
	}

	const handleDragEnd = () => {
		setDraggedItem(null)
		setDropTarget(null)
	}

	// Búsqueda con debounce
	const handleBusqueda = (value) => {
		setFiltroBusqueda(value || '')
	}

	const debouncedFiltroBusqueda = React.useCallback(
		debounce(handleBusqueda, 400),
		[]
	)

	useEffect(() => {
		debouncedFiltroBusqueda(busqueda)
	}, [busqueda])

	useEffect(() => {
		if (paginaAct === 1) {
			refreshPreguntas()
		} else {
			setPaginaAct(1)
		}
	}, [filtroBusqueda, fetchDeACuantos, filtroEstado])

	useEffect(() => {
		if (!loading) {
			refreshPreguntas()
		}
	}, [paginaAct])

	useEffect(() => {
		refreshConfiguracion()
		refreshPreguntas()
	}, [])

	const cerrarAccion = () => {
		setAccionActual({ pregunta: null, accion: '' })
	}

	const accionesActuales = {
		handleVer: (pregunta) => setAccionActual({ pregunta, accion: 'ver' }),
		handleEditar: (pregunta) => setAccionActual({ pregunta, accion: 'editar' }),
		handleEliminar: (pregunta) => setAccionActual({ pregunta, accion: 'eliminar' })
	}

	// render
	return (
		<Container fluid id="faq" className="vh-100">
			<TopHeader
				iconName="question-circle"
				text="Preguntas Frecuentes"
			/>

			{/* Switch principal para habilitar/deshabilitar la sección */}
			<Row className="mb-4">
				<Col>
					<Alert variant={configuracion.habilitado ? "success" : "warning"} className="d-flex align-items-center justify-content-between">
						<div>
							<i className={`fa ${configuracion.habilitado ? 'fa-check-circle' : 'fa-exclamation-triangle'} me-2`}></i>
							<strong>Estado de la sección:</strong> {configuracion.habilitado ? 'Habilitada en el sitio web' : 'Deshabilitada (no visible en el sitio web)'}
						</div>
						<Form.Check
							type="switch"
							id="switch-habilitado"
							label={"Habilitado"}
							checked={configuracion.habilitado}
							onChange={(e) => toggleHabilitado(e.target.checked)}
						/>
					</Alert>
				</Col>
			</Row>

			{/* Filtros */}
			<Row className="d-flex no-gutters mb-3">
				<Col className="mt-2">
					<Form>
						<Row id="searchbar" className="justify-content-between align-items-center g-2">
							<Col sm={6} md={2}>
								<small>
									<span className="d-none d-sm-inline-block">Mostrar de a</span>
									<Form.Select size="sm" value={fetchDeACuantos} onChange={(e) => setFetchDeACuantos(e.target.value)} className="mx-1">
										{opcionesCuantosMostrar.map((o) => (
											<option value={o} key={`mostrar-${o}`}>
												{o}
											</option>
										))}
									</Form.Select>
									<span className="ms-2">preguntas</span>
								</small>
							</Col>

							<Col md={4} lg={3}>
								<Form.Group controlId="txtBusqueda">
									<InputGroup>
										<Form.Control
											autoFocus
											value={busqueda}
											onChange={(e) => setBusqueda(e.target.value)}
											type="text"
											placeholder="Buscar en preguntas y respuestas..."
											aria-label="Buscar"
										/>
										<InputGroup.Text><i className="fa fa-search"></i></InputGroup.Text>
									</InputGroup>
								</Form.Group>
							</Col>

							<Col md={2} lg={2}>
								<Form.Group controlId="selectEstado">
									<Form.Select
										value={filtroEstado}
										onChange={(e) => setFiltroEstado(e.target.value)}
									>
										<option value="">Todos los estados</option>
										<option value="activo">Solo activas</option>
										<option value="inactivo">Solo inactivas</option>
									</Form.Select>
								</Form.Group>
							</Col>
						</Row>
					</Form>
				</Col>
			</Row>

			{/* Botón Nueva pregunta */}
			<Row className="mb-3">
				<Col>
					<Button variant="primary" onClick={() => setAccionActual({ pregunta: null, accion: 'nueva' })}>
						<i className="fa fa-plus"></i> Nueva pregunta
					</Button>
				</Col>
			</Row>

			{/* Tabla de preguntas */}
			<Row>
				<Col>
					<div className="faq-tabla">
						<small>Total resultados: {loading ? (<Spinner animation="border" size="sm" className="ms-2" />) : (<strong>{numResultados}</strong>)}</small>

						<Table striped bordered hover responsive className="mt-2 tabla-faq">
							<thead>
								<tr>
									<th className="drag-handle-header"></th>
									<th className="orden-header text-center">Orden</th>
									<th className="pregunta-header">Pregunta</th>
									<th className="estado-header text-center">Estado</th>
									<th className="acciones-header">Acciones</th>
								</tr>
							</thead>
							<tbody>
								{loading ? (
									<tr>
										<td colSpan="5" className="text-center py-4">
											<Spinner animation="border" variant="primary" />
										</td>
									</tr>
								) : (
									preguntas.length > 0 ? preguntas.map((pregunta) => (
										<tr
											key={pregunta.id}
											className={`pregunta-row ${draggedItem?.id === pregunta.id ? 'dragging' : ''} ${dropTarget === pregunta.id ? 'drop-target' : ''}`}
											draggable={true}
											onDragStart={(e) => handleDragStart(e, pregunta)}
											onDragOver={(e) => handleDragOver(e, pregunta)}
											onDragLeave={handleDragLeave}
											onDrop={(e) => handleDrop(e, pregunta)}
											onDragEnd={handleDragEnd}
										>
											<td className="drag-handle-cell">
												<i className="fa fa-grip-vertical drag-handle"></i>
											</td>
											<td className="orden-cell text-center">
												<Badge bg="secondary">{pregunta.orden}</Badge>
											</td>
											<td className="pregunta-cell">
												<div className="pregunta-texto">{pregunta.pregunta}</div>
											</td>
											<td className="estado-cell text-center">
												<Form.Check
													type="switch"
													id={`switch-${pregunta.id}`}
													checked={pregunta.activo}
													onChange={(e) => toggleEstadoPregunta(pregunta.id, e.target.checked)}
													label={pregunta.activo ? 'Activa' : 'Inactiva'}
												/>
											</td>
											<td className="acciones-cell">
												<DropdownButton
													as="div"
													title={<i className="fa fa-ellipsis-v"></i>}
													id={`dropdown-pregunta-${pregunta.id}`}
													size="sm"
													variant="outline-secondary"
												>
													<Dropdown.Item onClick={() => accionesActuales.handleVer(pregunta)}>
														<i className="fa fa-eye me-2"></i>
														Ver
													</Dropdown.Item>
													<Dropdown.Item onClick={() => accionesActuales.handleEditar(pregunta)}>
														<i className="fa fa-pencil me-2"></i>
														Editar
													</Dropdown.Item>
													<Dropdown.Divider />
													<Dropdown.Item onClick={() => accionesActuales.handleEliminar(pregunta)} className="text-danger">
														<i className="fa fa-trash me-2"></i>
														Eliminar
													</Dropdown.Item>
												</DropdownButton>
											</td>
										</tr>
									)) : (
										<tr>
											<td colSpan="5" className="text-center py-4">
												No se encontraron preguntas con los filtros aplicados.
											</td>
										</tr>
									)
								)}
							</tbody>
						</Table>

						{/* Paginación */}
						{preguntas.length > 0 && (
							<Paginacion
								pagina={paginaAct}
								numPaginas={Math.ceil(numResultados / fetchDeACuantos) || 1}
								setPagina={setPaginaAct}
							/>
						)}
					</div>
				</Col>
			</Row>

			{/* Componentes para acciones CRUD */}
			<NuevaPregunta
				mostrar={accionActual.accion === 'nueva'}
				guardar={nuevaPregunta}
				cancelar={cerrarAccion}
			/>

			<VerPregunta
				mostrar={accionActual.accion === 'ver'}
				pregunta={accionActual.pregunta}
				cancelar={cerrarAccion}
			/>

			<EditarPregunta
				mostrar={accionActual.accion === 'editar'}
				preguntaOriginal={accionActual.pregunta}
				guardar={editarPregunta}
				cancelar={cerrarAccion}
			/>

			<EliminarPregunta
				mostrar={accionActual.accion === 'eliminar'}
				pregunta={accionActual.pregunta}
				cancelar={cerrarAccion}
				eliminar={eliminarPregunta}
			/>
		</Container>
	)
}

export default Faq